import React, { useState, useEffect } from "react";
import "./PaymentCard.css";
import { InputAdornment, TextField, IconButton } from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { getPaymentModeStyles } from "../../collectionUtils";
import { truncateToTwoDecimals } from "../../../../components/dms_common/DmsUtils";

export interface PaymentCardProps {
  paymentDate: string;
  paidAmount: string;
  finalAmount: string;
  paymentMode: string;
  shortageAmount: string;
  status: string;
  handleApprove: (shortageValue: string) => void;
  handleReject: () => void;
}

const PaymentCard: React.FC<PaymentCardProps> = ({
  paymentDate,
  paidAmount,
  finalAmount,
  paymentMode,
  shortageAmount,
  status,
  handleApprove,
  handleReject,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [displayFinalAmount, setDisplayFinalAmount] = useState(finalAmount);

  // Parse paidAmount to a number for calculations
  const paidAmountValue = parseFloat(paidAmount.replace("₹ ", "")) || 0;

  // Update final amount in real-time based on inputValue
  useEffect(() => {
    const shortage = parseFloat(inputValue) || 0;
    const newFinalAmount = paidAmountValue - shortage;
    setDisplayFinalAmount(`₹ ${truncateToTwoDecimals(newFinalAmount)}`);
  }, [inputValue, paidAmountValue]);

const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  const value = e.target.value;
  // This regex only allows up to "1.23"
  const regex = /^\d*(\.\d{0,2})?$/; 

  if (value === "" || regex.test(value)) {
    setInputValue(value);
  }
};

  const handleConfirm = () => {
    if (inputValue === "" || !isNaN(parseFloat(inputValue))) {
      handleApprove(inputValue);
      setInputValue("");
    }
  };

  const handleCancel = () => {
    setInputValue("");
    handleReject();
  };

  const isEditable = status === "PENDING";
  const isConfirmDisabled = inputValue !== "" && parseFloat(inputValue) > paidAmountValue;

  return (
    <div className="PaymentCard_container">
      <div className="PaymentCard_upper">
        <div className="PaymentCard_upper_container">
          <div className="PaymentCard_upper_container_item">
            <span className="PaymentCard_upper_container_item_label">
              Payment date
            </span>
            <span className="PaymentCard_upper_container_item_value">
              {paymentDate}
            </span>
          </div>
          <div className="PaymentCard_upper_container_item">
            <span className="PaymentCard_upper_container_item_label">
              Paid by
            </span>
            <div
              className="PaymentCard_paymentMode"
              style={{
                ...getPaymentModeStyles(paymentMode),
              }}
            >
              {paymentMode}
            </div>
          </div>
        </div>
        <div className="PaymentCard_upper_container">
          <div className="PaymentCard_upper_container_item">
            <span className="PaymentCard_upper_container_item_label">
              Paid amount
            </span>
            <span className="PaymentCard_upper_container_item_value">
              {paidAmount}
            </span>
          </div>
          <div className="PaymentCard_upper_container_item">
            <span className="PaymentCard_upper_container_item_label">
              Final amount
            </span>
            <span
              className="PaymentCard_upper_container_item_value"
              style={{ fontSize: "14px", fontWeight: 550 }}
            >
              {isEditable ? displayFinalAmount : finalAmount}
            </span>
          </div>
        </div>
      </div>

      <div className="PaymentCard_lower">
        <span className="PaymentCard_lower_label">
          {isEditable ? "Enter shortage amount" : "Shortage amount"}
        </span>
        <div className="PaymentCard_lower_input_container">
          {isEditable ? (
            <>
              <TextField
                sx={{
                  "& .MuiOutlinedInput-root": {
                    border: "1px solid #F3F3F3",
                    borderRadius: "8px",
                    height: "40px",
                    "& fieldset": {
                      border: "none",
                    },
                    "&:hover fieldset": {
                      border: "none",
                    },
                    "&.Mui-focused fieldset": {
                      border: "none",
                    },
                  },
                  "& .MuiInputAdornment-root": {
                    "& .MuiTypography-root": {
                      fontSize: "0.75rem",
                      color: "#7A7A7A",
                    },
                  },
                }}
                variant="outlined"
                value={inputValue}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">₹</InputAdornment>
                  ),
                }}
                inputProps={{
                  inputMode: "decimal",
                }}
              />
              <div className="PaymentCard_lower_buttons_container">
                <IconButton onClick={handleCancel} sx={{ color: "#EA3423" }}>
                  <CancelIcon />
                </IconButton>
                <IconButton
                  onClick={handleConfirm}
                  sx={{ color: "#5D9D51" }}
                  disabled={isConfirmDisabled}
                >
                  <CheckCircleIcon />
                </IconButton>
              </div>
            </>
          ) : (
            <TextField
              sx={{
                "& .MuiOutlinedInput-root": {
                  border: "1px solid #F3F3F3",
                  borderRadius: "8px",
                  height: "40px",
                  "& fieldset": {
                    border: "none",
                  },
                  "&:hover fieldset": {
                    border: "none",
                  },
                  "&.Mui-focused fieldset": {
                    border: "none",
                  },
                },
                "& .MuiInputAdornment-root": {
                  "& .MuiTypography-root": {
                    fontSize: "0.75rem",
                    color: "#7A7A7A",
                  },
                },
              }}
              variant="outlined"
              value={shortageAmount === "" ? "0.00" : shortageAmount}
              disabled
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">₹</InputAdornment>
                ),
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentCard;